import React, { useState, useEffect, useRef } from "react";

import galleryImagesBase from "../../data/gallery.json";

// Shuffle array helper
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});

  // Randomize order and assign sizes on mount
  const [galleryImages] = useState(() => {
    const shuffled = shuffleArray(galleryImagesBase);
    // Assign sizes: every 4th image is wide to create visual interest without gaps
    return shuffled.map((img, index) => ({
      ...img,
      size: index % 4 === 0 ? "wide" : "normal"
    }));
  });
  const [visibleItems, setVisibleItems] = useState(new Set());
  const itemRefs = useRef([]);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleItems((prev) => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setSelectedImage(null);
      if (e.key === "ArrowRight" && selectedImage !== null) {
        setSelectedImage((prev) => (prev + 1) % galleryImages.length);
      }
      if (e.key === "ArrowLeft" && selectedImage !== null) {
        setSelectedImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage]);

  // Lock body scroll when lightbox open
  useEffect(() => {
    if (selectedImage !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedImage]);

  const handleImageLoad = (index) => {
    setLoadedImages((prev) => ({ ...prev, [index]: true }));
  };

  const openLightbox = (index) => setSelectedImage(index);
  const closeLightbox = () => setSelectedImage(null);

  const goToPrevious = (e) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev + 1) % galleryImages.length);
  };

  return (
    <section className="gallery-section" id="gallery-section">
      {/* Header */}
      <div className="gallery-hero">
        <div className="gallery-hero-content">
          <h2>Our Work</h2>
          <div className="gallery-divider"></div>
          <p>Clean fades. Precision cuts. Every detail matters.</p>
        </div>
      </div>

      {/* Grid gallery */}
      <div className="gallery-container">
        <div className="gallery-grid">
          {galleryImages.map((image, index) => (
            <div
              key={index}
              ref={(el) => (itemRefs.current[index] = el)}
              data-index={index}
              className={`gallery-card gallery-card--${image.size} ${loadedImages[index] ? "loaded" : ""
                } ${visibleItems.has(index) ? "visible" : ""}`}
              style={{ animationDelay: `${index * 0.08}s` }}
              onClick={() => openLightbox(index)}
            >
              <div className="gallery-card-inner">
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  onLoad={() => handleImageLoad(index)}
                />
                <div className="gallery-card-overlay">
                  <div className="gallery-card-content">
                    <span className="gallery-card-icon">
                      <i className="fas fa-plus"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Book Now CTA */}
        <div className="gallery-cta">
          <p>Like what you see?</p>
          <a
            href="https://getsquire.com/booking/book/evergreen-barbershop-san-jose"
            target="_blank"
            rel="noopener noreferrer"
            className="gallery-book-btn"
          >
            Book Now
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>

          <button className="lightbox-nav lightbox-prev" onClick={goToPrevious} aria-label="Previous">
            <i className="fas fa-chevron-left"></i>
          </button>

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[selectedImage].src}
              alt={galleryImages[selectedImage].alt}
            />
            <div className="lightbox-info">
              <span className="lightbox-counter">
                {selectedImage + 1} / {galleryImages.length}
              </span>
            </div>
          </div>

          <button className="lightbox-nav lightbox-next" onClick={goToNext} aria-label="Next">
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}
    </section>
  );
}

export default Gallery;
